import { useMemo, useState } from 'react';
import type { FC } from 'react';
import { Flex, Radio, useRadio } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { RadioProps } from '@chakra-ui/react';
import {
    getDistanceDescription,
    getTodaysStoreClosingTime,
    isStoreOpen,
} from '../../../bff-api/utils/store-utils';
import { Button, Text } from '../../../ui-components';
import { toAddressDescription } from '../../../utils/address-utils';
import { GetIcon } from '../../icons';
import { StoreHours } from '../../store-hours';
import type { Store } from '../../../bff-api/stores/model';

type StoreDisplayProps = RadioProps & { store: Store; ignoreInventory?: boolean };
export const BopisStoreSearchItem: FC<StoreDisplayProps> = ({
    store,
    ignoreInventory,
    ...radioProps
}) => {
    const intl = useIntl();
    const { getRadioProps, htmlProps } = useRadio(radioProps);
    const [showInfo, setShowInfo] = useState(false);

    const { id, name, distance, distanceUnit, phone, c_bopisAvailability } = store;

    const storeInfoButton = useMemo(
        () => (
            <Button
                id={`store-${id}-store-info-button`}
                onClick={(): void => setShowInfo(!showInfo)}
                rightIcon={
                    showInfo ? (
                        <GetIcon name="dropdown-arrow-close" size="md" />
                    ) : (
                        <GetIcon name="dropdown-arrow-open" size="md" />
                    )
                }
                variant="text-button"
            >
                <FormattedMessage id="store.info" defaultMessage="Store info" />
            </Button>
        ),
        [id, showInfo],
    );

    const isOpen = isStoreOpen(store);
    const closingTime = getTodaysStoreClosingTime(store);
    const distanceDescription = getDistanceDescription(distance, distanceUnit);
    const description = isOpen
        ? `- ${intl.formatMessage({
              id: 'store.item.now.open',
              defaultMessage: 'Now open',
          })} ${distanceDescription}`
        : distanceDescription;

    return (
        <Flex data-testid={`store-${id}`} gap={4}>
            <Radio
                {...getRadioProps()}
                data-testid={`store-${id}-radio-button`}
                key={`${id}-radio-button`}
                isDisabled={!ignoreInventory && !c_bopisAvailability}
                value={store.id}
            />

            <Flex direction="column" width="100%" gap={3} {...htmlProps}>
                <Flex direction="column" gap={1}>
                    <Text
                        id={`store-${id}-name`}
                        style="bold"
                        color="slt-pepper.1000"
                    >{`${name} ${description}`}</Text>

                    <Text
                        id={`store-${id}-description`}
                        size="small"
                        margin="4px 0 0 0"
                        color="slt-pepper.1000"
                    >
                        {toAddressDescription(store)}
                    </Text>
                </Flex>

                <Flex justify="space-between">
                    {isOpen ? (
                        <Flex gap={1}>
                            <Text
                                id={`store-${id}-open-label`}
                                size="small"
                                color="slt-pepper.1000"
                            >
                                <FormattedMessage
                                    id="store.item.open.until"
                                    defaultMessage="Open until"
                                />
                            </Text>

                            <Text
                                id={`store-${id}-open-value`}
                                size="small"
                                style="bold"
                                color="slt-pepper.1000"
                            >
                                {closingTime}
                            </Text>
                        </Flex>
                    ) : (
                        <Flex gap={1}>
                            <Text
                                id={`store-${id}-store-is-label`}
                                size="small"
                                color="slt-pepper.1000"
                            >
                                <FormattedMessage
                                    id="store.item.store.id"
                                    defaultMessage="Store is"
                                />
                            </Text>

                            <Text
                                id={`store-${id}-closed-label`}
                                size="small"
                                style="bold"
                                color="slt-pepper.1000"
                            >
                                <FormattedMessage id="store.item.closed" defaultMessage="closed" />
                            </Text>
                        </Flex>
                    )}

                    {ignoreInventory && storeInfoButton}
                </Flex>

                {!ignoreInventory && (
                    <Flex
                        justifyContent={ignoreInventory ? 'flex-end' : 'space-between'}
                        alignItems="center"
                    >
                        {c_bopisAvailability && c_bopisAvailability >= 0 ? (
                            <Flex gap={1} alignItems="center">
                                <Text
                                    id={`store-${id}-in-stock-label`}
                                    size="small"
                                    style="bold"
                                    color="slt-success-green.1000"
                                >
                                    <FormattedMessage
                                        id="store.item.in.stock"
                                        defaultMessage="In Stock"
                                    />
                                </Text>
                                -
                                <Text
                                    id={`store-${id}-in-stock-value`}
                                    size="small"
                                    style="bold"
                                    color="slt-pepper.1000"
                                >
                                    <FormattedMessage
                                        id="store.item.only.x.left"
                                        defaultMessage="Only {quantity} left"
                                        values={{ quantity: c_bopisAvailability }}
                                    />
                                </Text>
                            </Flex>
                        ) : (
                            <Text
                                id={`store-${id}-unavailable-label`}
                                size="small"
                                style="bold"
                                margin="4px 0 0 0"
                                color="slt-pepper.1000"
                            >
                                <FormattedMessage
                                    id="store.item.unavailable"
                                    defaultMessage="Unavailable"
                                />
                            </Text>
                        )}

                        {storeInfoButton}
                    </Flex>
                )}

                {showInfo ? (
                    <Flex direction="column" gap={2}>
                        <StoreHours id={`store-${id}-hours`} store={store} />

                        <Flex direction="column" gap={1}>
                            <Text
                                id={`store-${id}-phone-label`}
                                style="bold"
                                color="slt-pepper.1000"
                            >
                                <FormattedMessage id="store.item.phone" defaultMessage="Phone" />
                            </Text>

                            <Text
                                id={`store-${id}-phone-value`}
                                size="small"
                                color="slt-pepper.1000"
                            >
                                {phone}
                            </Text>
                        </Flex>
                    </Flex>
                ) : null}
            </Flex>
        </Flex>
    );
};

BopisStoreSearchItem.defaultProps = {
    ignoreInventory: false,
};
